import {
  List,
  ActionIcon,
  Flex,
  Divider,
  Text,
  useMantineTheme,
  Box,
} from "@mantine/core";
import { IconPlaylistAdd } from "@tabler/icons";
import {
  CategoryOptionType,
  useUpdateCategoryOptionMutation,
} from "src/generated/graphql";
import useTransitionMenu from "src/hooks/useTransitionMenu";

export interface ICategoryOptionAction {
  id: string;
  name: string;
  type: CategoryOptionType;
}
interface CategoryOptionListProps {
  type: CategoryOptionType;
  options: { name: string; id: string; isActive: boolean }[] | undefined;
  onDelete: ({ id, name, type }: ICategoryOptionAction) => void;
  onEdit: ({ id, name, type }: ICategoryOptionAction) => void;
  onCreate: (type: CategoryOptionType) => void;
  isAllowWrite: boolean;
  refetch: () => void;
}

function CategoryOptionList({
  type,
  options,
  onDelete,
  onEdit,
  onCreate,
  isAllowWrite,
  refetch,
}: CategoryOptionListProps) {
  const { TransitionMenu, setActiveOption, ShowActivation } =
    useTransitionMenu();
  const theme = useMantineTheme();
  let color = theme.colors.secondaryTextColor[6];

  const handleOpenListMenu = (option: { name: string; id: string }) => {
    setActiveOption(option);
  };

  const addActionIcon = (
    <ActionIcon
      disabled={!isAllowWrite}
      size="lg"
      onClick={() => onCreate(type)}
    >
      <IconPlaylistAdd color={color} />
    </ActionIcon>
  );

  const [UpdateOption] = useUpdateCategoryOptionMutation({
    onCompleted: () => {
      refetch();
    },
  });
  const onUpdateActivation = (id: string, isActive: boolean) => {
    UpdateOption({
      variables: { updateCategoryOptionId: id, data: { isActive } },
    });
  };

  return (
    <Box>
      {!options ? (
        <Flex
          justify="center"
          align="center"
          style={{
            backgroundColor: theme.colors.secondary[6],
          }}
        >
          <Text color={color}>Click</Text>&nbsp;{addActionIcon}&nbsp;
          <Text color={color}>to add an item</Text>
        </Flex>
      ) : (
        <List w="100%" listStyleType="none" style={{ color }}>
          {options.map((item, index) => (
            <List.Item
              w="100%"
              icon={null}
              key={item.id}
              c={color}
              sx={{
                "&": {
                  position: "relative",
                },
                "& ::marker": {
                  content: "none",
                },
                "& .mantine-List-itemWrapper": {
                  display: "flex",
                },
                "& .mantine-List-itemWrapper > span > div": {
                  alignItems: "center",
                },
              }}
            >
              {!!index && (
                <Divider style={{ borderColor: theme.colors.gray[2] }} />
              )}
              <Flex align="center" justify="space-between" my={3}>
                <Text m={10}>{item.name as string}</Text>

                {ShowActivation(item.isActive, "sm")}

                {isAllowWrite && (
                  <TransitionMenu
                    fullWidth={true}
                    onClick={() => handleOpenListMenu(item)}
                    onEdit={() =>
                      onEdit({
                        ...item,
                        type,
                      })
                    }
                    onDeleteCustom={() =>
                      onDelete({
                        ...item,
                        type,
                      })
                    }
                    id={item.id}
                    isActive={item.isActive}
                    onUpdateActivation={onUpdateActivation}
                  />
                )}
              </Flex>
            </List.Item>
          ))}
        </List>
      )}
    </Box>
  );
}

export default CategoryOptionList;
